import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery, useMutation } from '@apollo/client';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { INTENTION_TYPE } from 'constants/search';
import { CLASSES_OF_SERVICE_BY_TYPE, PLUS, PREMIER, STANDARD } from 'constants/classOfService';
import { BUSINESS_BENEFIT_PLUS_CODES, BUSINESS_BENEFIT_PREMIER_CODES } from 'constants/businessBenefitUpgrade';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { isUserCallCenterAgent, isUserStationAgent } from 'utils/user';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';
import AgentPortalError from 'errors/AgentPortalError';

import { GET_JOURNEY_SEARCH_FOR_EXCHANGE, GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS } from 'store/queries';
import { ADD_ADDITIONAL_DETAILS, OVERRIDE_PRICE, REVERT_CHANGE } from 'store/mutations';
import useRebook from 'components/mutations/useRebook';
import NotLoggedInRedirectButton from 'pages/BuyThalysPass/NotLoggedInRedirectButton';
import Modal from 'components/ui/Modal';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

import {
  runAfterSaleValidator,
  bookingIsConfirmed,
  allSegmentsOfProductFamily,
  allSegmentsOfService,
  allSegmentsSegmentsDepartInFuture,
  allSegmentsOfType,
  getSegmentsForItemRefs,
  selectedItemsHaveLuggageExtras
} from '../AfterSalesCtas.utils';
import { buildUpgradeSearchPassengers, findEarliestLeg } from '../UpgradePointsCta/UpgradePointsCta.utils';
import { getUpgradeFare } from './BusinessBenefitUpgradeCta.utils';
import { StyledCtaButtons } from '../AfterSalesCtas.styles';

const BUSINESS_BENEFIT_PRODUCT_FAMILY_CODE = 'E4B_RWD';
const BUSINESS_BENEFIT_TYPE_NAME = 'Ticket Upgrade';
const BUSINESS_BENEFIT_STATUS_AVAILABLE = 'Available';
const BUSINESS_STANDARD_BENEFIT_NAME = 'Ticket Upgrade - Business Standard';
const BUSINESS_PLUS_BENEFIT_NAME = 'Ticket Upgrade - Business Plus';

const buildUpgradeSegments = (fare) =>
  fare.legs.map((leg) => ({
    origin: leg.origin.uic,
    destination: leg.destination.uic,
    direction: fare.direction,
    departureDate: leg.timing.departureDate,
    serviceName: leg.serviceName,
    serviceIdentifier: leg.serviceIdentifier,
    items: leg.products
      .filter((product) => product.passengerId)
      .map((product) => ({
        passengerId: product.passengerId,
        tariffCode: product.tariffCode
      }))
  }));

const buildOverridePayloads = ({ pendingCancellationLegs = [], pendingCreationLegs = [] } = {}) => {
  const overrideItems = [];
  const additionalDetails = [];

  pendingCancellationLegs.forEach((cancelLeg, legIndex) => {
    const createLeg = pendingCreationLegs?.[legIndex];

    cancelLeg?.bookingItems?.forEach(({ passengerId, price }) => {
      const createItem = createLeg?.bookingItems?.find((item) => item.passengerId === passengerId);

      if (createItem?.itemRef && price !== undefined && price !== null) {
        overrideItems.push({
          itemRefId: Number(createItem.itemRef),
          price
        });
        additionalDetails.push({
          itemRefId: Number(createItem.itemRef),
          key: 'original_price',
          value: String(price)
        });
      }
    });
  });

  return { overrideItems, additionalDetails };
};

const BusinessBenefitUpgradeCta = ({
  history: {
    push,
    location: { search }
  },
  history,
  label,
  disabled,
  selectedItemRefs,
  booking,
  user,
  isMenuOpen = false,
  setDisabled
}) => {
  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const [showNotLoggedIn, setShowNotLoggedIn] = useState(false);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);
  const [showNoBenefitsModal, setShowNoBenefitsModal] = useState(false);
  const [noBenefitsMessageId, setNoBenefitsMessageId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const overrideFailureRef = useRef(false);
  const businessIdRef = useRef(null);

  const segments = useMemo(() => [...getSegmentsForItemRefs(booking, selectedItemRefs)], [booking, selectedItemRefs]);
  const filteredOutbound = useMemo(
    () => filterLegsByProductItemRefs(booking.outbound, selectedItemRefs),
    [booking.outbound, selectedItemRefs]
  );
  const filteredInbound = useMemo(
    () => filterLegsByProductItemRefs(booking.inbound, selectedItemRefs),
    [booking.inbound, selectedItemRefs]
  );
  const outboundEarliestLeg = useMemo(() => findEarliestLeg(filteredOutbound), [filteredOutbound]);
  const inboundEarliestLeg = useMemo(() => findEarliestLeg(filteredInbound), [filteredInbound]);

  const allowedClasses = useMemo(() => [...new Set([...CLASSES_OF_SERVICE_BY_TYPE[STANDARD], ...CLASSES_OF_SERVICE_BY_TYPE[PLUS]])], []);
  const isStandardClass =
    segments.length > 0 && segments.every((segment) => CLASSES_OF_SERVICE_BY_TYPE[STANDARD].includes(segment.classOfService?.code));
  const isPlusClass =
    segments.length > 0 && segments.every((segment) => CLASSES_OF_SERVICE_BY_TYPE[PLUS].includes(segment.classOfService?.code));
  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  const upgradeConfig = useMemo(() => {
    if (isStandardClass) {
      return {
        benefitName: BUSINESS_STANDARD_BENEFIT_NAME,
        noBenefitsMessageId: 'afterSales.businessBenefitUpgrade.noPlusVouchers',
        tariffCodes: BUSINESS_BENEFIT_PLUS_CODES,
        targetClasses: CLASSES_OF_SERVICE_BY_TYPE[PLUS]
      };
    }

    if (isPlusClass) {
      return {
        benefitName: BUSINESS_PLUS_BENEFIT_NAME,
        noBenefitsMessageId: 'afterSales.businessBenefitUpgrade.noPremierVouchers',
        tariffCodes: BUSINESS_BENEFIT_PREMIER_CODES,
        targetClasses: CLASSES_OF_SERVICE_BY_TYPE[PREMIER]
      };
    }

    return null;
  }, [isPlusClass, isStandardClass]);

  const isEurostarOnly = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsOfService([serviceTypeCodes.EUROSTAR])]);
  const isGreenspeedOnly = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsOfService([serviceTypeCodes.GREENSPEED])]);
  const isServiceEligible = isEurostarOnly || isGreenspeedOnly;
  const isUserEligible = isUserStationAgent(user) || isUserCallCenterAgent(user);

  const isBusinessBenefitUpgradePossible =
    selectedItemRefs.length === 1 &&
    isServiceEligible &&
    isUserEligible &&
    upgradeConfig &&
    runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      bookingIsConfirmed,
      allSegmentsOfProductFamily([productFamilyCodes.PUB]),
      allSegmentsSegmentsDepartInFuture,
      allSegmentsOfType(allowedClasses)
    ]);

  const [getBusinessBenefits, { loading: businessBenefitsLoading, error: businessBenefitsError }] = useLazyQuery(
    GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (!upgradeConfig) {
          return;
        }

        const benefitsPayload = data?.getLoyaltyBusinessAccountBenefits;
        businessIdRef.current = benefitsPayload?.businessId ?? benefitsPayload?.businessID ?? businessIdRef.current;
        const benefits = benefitsPayload?.benefitList ?? [];
        const availableCount = benefits.filter(
          (benefit) =>
            benefit?.benefitTypeName === BUSINESS_BENEFIT_TYPE_NAME &&
            benefit?.benefitName === upgradeConfig.benefitName &&
            benefit?.benefitStatus === BUSINESS_BENEFIT_STATUS_AVAILABLE
        ).length;

        if (!benefitsPayload || availableCount === 0) {
          setNoBenefitsMessageId(upgradeConfig.noBenefitsMessageId);
          setShowNoBenefitsModal(true);
          return;
        }

        sendJourneySearch();
      }
    }
  );

  const [journeySearch, { loading: journeySearchLoading, error: journeySearchError }] = useLazyQuery(GET_JOURNEY_SEARCH_FOR_EXCHANGE, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      if (!upgradeConfig) {
        return;
      }

      try {
        const outboundFare = outboundEarliestLeg
          ? getUpgradeFare(
              outboundEarliestLeg,
              data?.journeySearchForExchange?.outbound,
              false,
              upgradeConfig.targetClasses,
              upgradeConfig.tariffCodes
            )
          : null;
        const inboundFare = inboundEarliestLeg
          ? getUpgradeFare(
              inboundEarliestLeg,
              data?.journeySearchForExchange?.inbound,
              true,
              upgradeConfig.targetClasses,
              upgradeConfig.tariffCodes
            )
          : null;

        if ((outboundEarliestLeg && !outboundFare) || (inboundEarliestLeg && !inboundFare)) {
          setNoBenefitsMessageId('afterSales.businessBenefitUpgrade.noFares');
          setShowNoBenefitsModal(true);

          return;
        }

        const selectedFare = outboundFare || inboundFare;

        const rebookResult = await rebook({
          variables: {
            bookingReference: booking.reference,
            currency: booking.currency,
            segments: buildUpgradeSegments(selectedFare),
            overrideAftersalesFees: true,
            overrideBookingLimits: false,
            overrideAftersalesRules: false,
            isFlowForward: false,
            isExchangingOFETrain: false
          },
          isPackageBooking: booking.isPackage
        });

        const { overrideItems, additionalDetails } = buildOverridePayloads(rebookResult?.rebook);

        if (overrideItems.length > 0) {
          try {
            await overridePrice({
              variables: {
                reference: booking.reference,
                items: overrideItems
              }
            });
          } catch (error) {
            overrideFailureRef.current = true;
            try {
              await revertChange({
                variables: {
                  bookingReference: booking.reference
                }
              });
            } catch (revertError) {
              setLocalError(revertError);
              return;
            }

            setLocalError(error);
            return;
          }
        }

        if (additionalDetails.length > 0) {
          await addAdditionalDetails({
            variables: {
              bookingReference: booking.reference,
              additionalDetails
            }
          });
        }

        const params = new URLSearchParams();
        if (membershipNumber) {
          params.set('membershipNumber', membershipNumber);
        }
        if (upgradeConfig?.benefitName) {
          params.set('businessBenefitName', upgradeConfig.benefitName);
        }
        if (businessIdRef.current) {
          params.set('businessId', businessIdRef.current);
        }
        params.set('isBusinessBenefitUpgrade', 'true');

        push(`/exchange/${booking.reference}/pax?${params.toString()}`);
      } catch (error) {
        setLocalError(error);
      }
    }
  });

  const [rebook, { loading: rebookLoading, error: rebookError }] = useRebook();
  const [overridePrice, { loading: overridePriceLoading, error: overridePriceError }] = useMutation(OVERRIDE_PRICE);
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading, error: addAdditionalDetailsError }] =
    useMutation(ADD_ADDITIONAL_DETAILS);
  const [revertChange] = useMutation(REVERT_CHANGE);

  if (localError) {
    throw new AgentPortalError(localError);
  }

  if (
    businessBenefitsError ||
    journeySearchError ||
    rebookError ||
    (overridePriceError && !overrideFailureRef.current) ||
    addAdditionalDetailsError
  ) {
    throw new AgentPortalError(
      businessBenefitsError ||
        journeySearchError ||
        rebookError ||
        (overridePriceError && !overrideFailureRef.current) ||
        addAdditionalDetailsError
    );
  }

  const isLoading = businessBenefitsLoading || journeySearchLoading || rebookLoading || overridePriceLoading || addAdditionalDetailsLoading;

  useEffect(() => {
    setDisabled(!isBusinessBenefitUpgradePossible || isLoading);
  });

  function sendJourneySearch() {
    const variables = {
      reference: booking.reference,
      intention: INTENTION_TYPE.UPGRADE,
      exchangeItems: selectedItemRefs.map(Number),
      currency: booking.currency,
      passengersList: buildUpgradeSearchPassengers(booking, selectedItemRefs),
      useNewLoyaltySystem: true,
      productFamilies: [BUSINESS_BENEFIT_PRODUCT_FAMILY_CODE]
    };

    const outboundLastLeg = filteredOutbound[filteredOutbound.length - 1];
    const inboundLastLeg = filteredInbound[filteredInbound.length - 1];

    if (outboundEarliestLeg) {
      variables.origin = outboundEarliestLeg.origin.uic;
      variables.destination = outboundLastLeg.addOnLeg ? outboundLastLeg.addOnLeg.destination.uic : outboundLastLeg.destination.uic;
      variables.outboundDate = outboundEarliestLeg.timing.date;

      if (inboundEarliestLeg) {
        variables.inboundDate = inboundEarliestLeg.timing.date;
        variables.returningOrigin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
        variables.returningDestination = inboundLastLeg.destination.uic;
      }
    } else if (inboundEarliestLeg) {
      variables.origin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
      variables.destination = inboundLastLeg.addOnLeg ? inboundLastLeg.addOnLeg.destination.uic : inboundLastLeg.destination.uic;
      variables.inboundDate = inboundEarliestLeg.timing.date;
    }

    journeySearch({ variables });
  }

  const handleClick = () => {
    if (!membershipNumber) {
      setShowNotLoggedIn(true);
      return;
    }

    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    if (upgradeConfig) {
      getBusinessBenefits({
        variables: {
          membershipNumber
        }
      });
    }
  };

  return (
    <>
      <StyledCtaButtons
        type="button"
        data-cy="business-benefit-upgrade-cta"
        data-testid="business-benefit-upgrade-cta"
        disabled={disabled}
        label={label}
        loading={isLoading}
        onClick={handleClick}
        isMenuOpen={isMenuOpen}
      >
        {label}
      </StyledCtaButtons>
      <Modal isOpen={showNotLoggedIn} onClose={() => setShowNotLoggedIn(false)}>
        <NotLoggedInRedirectButton history={history} />
      </Modal>
      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={() => setShowBlockTicketsWithExtraLuggageWarning(false)}
      />
      <Modal
        isOpen={showNoBenefitsModal}
        onClose={() => setShowNoBenefitsModal(false)}
        alert={noBenefitsMessageId ? <FormattedMessage id={noBenefitsMessageId} /> : null}
      ></Modal>
    </>
  );
};

BusinessBenefitUpgradeCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.element,
  disabled: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  setDisabled: PropTypes.func
};

export default BusinessBenefitUpgradeCta;
